import React from 'react';
import { Avatar, Button } from '@mui/material';
import {
  Business as BusinessIcon,
  Email,
  Language,
  Person,
  Phone,
  Visibility,
} from '@mui/icons-material';
import { Business } from '../../../validation/admin.validation';
import { maskEINLast4 } from '../../../helper/einMask.helper';

type Props = {
  businesses: Business[];
  onViewDetail: (id: string) => void;
};

const getOwnerName = (business: Business) =>
  [business.ownerFirstName, business.ownerMiddleName, business.ownerLastName]
    .filter(Boolean)
    .join(' ') || '-';

const getWebsiteHref = (website: string) =>
  website?.startsWith('http') ? website : `https://${website}`;

const BusinessCardView: React.FC<Props> = ({ businesses, onViewDetail }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 mb-6 lg:mb-8">
      {businesses.map((business) => (
        <div
          key={business.id}
          className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden"
        >
          <div className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 truncate">{business.name}</h3>
                <p className="text-xs text-gray-500 truncate">EIN: {maskEINLast4(business.ein)}</p>

              </div>
              <Avatar
                sx={{
                  bgcolor: '#eff6ff',
                  color: '#2563eb',
                  width: { xs: 36, sm: 42 },
                  height: { xs: 36, sm: 42 },
                }}
              >
                <BusinessIcon fontSize="small" />
              </Avatar>
            </div>

            <div className="space-y-3">
              <div className="flex items-center min-w-0 text-sm">
                <Person className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600 mr-1">Owner:</span>
                <span className="font-medium text-gray-900 truncate">{getOwnerName(business)}</span>
              </div>

              <div className="flex items-center min-w-0 text-sm">
                <Phone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600 mr-1">Phone:</span>
                <span className="font-medium text-gray-900 truncate">{business.phone || 'Not provided'}</span>
              </div>

              <div className="flex items-center min-w-0 text-sm">
                <Email className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600 mr-1">Email:</span>
                <span className="font-medium text-gray-900 truncate">{business.email || '-'}</span>
              </div>

              <div className="flex items-center min-w-0 text-sm">
                <Language className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600 mr-1">Website:</span>
                {business.website ? (
                  <a
                    href={getWebsiteHref(business.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 hover:text-blue-800 underline truncate"
                  >
                    {business.website}
                  </a>
                ) : (
                  <span className="font-medium text-gray-900">Not provided</span>
                )}
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-5 pb-4 sm:pb-5">
            <Button
              fullWidth
              variant="contained"
              startIcon={<Visibility />}
              onClick={() => onViewDetail(business.id)}
              sx={{
                borderRadius: 2,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              View Details
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BusinessCardView;
